import clsx from "clsx";
import type { ReactNode, RefObject } from "react";
import { basisLabel, fromNet, rateFor, toNumber } from "../../shared/money";
import { ChoiceField, DraftFields, SelectField, TextField } from "../../web/components/fields";
import { Link } from "wouter";
import { InlinePicker } from "../../web/components/InlinePicker";
import { useMoney } from "../../web/lib/money";
import { type LabourUnit, MONEY_PATTERN, meta, type PriceListData, type PriceListSettings, type PricingRule, type RoundMode, type TaxQuote } from "./meta";
import { duration } from "./describe";
import { type Basis, hasLabourCost, labourRateNet, shownAmount, shownLabour, shownPercent, storedAmount, storedLabour, underMinimum } from "./pricing";
import { QuotedAmountField, quoteKey, quoteOptions } from "./QuotedAmount";

/*
 * Editing how prices are worked out: one editor for an item's own pricing (its Pricing tab) and a
 * group's (its window), so they look, feel and work the same. Both edit a draft, saved by
 * the bar their window puts underneath (`footer`): its fields update it as they're typed in (TextField
 * `live`), so the bar shows at once, under them, without moving the one being typed in. The layout:
 *
 *   Price is   [ Set price | Cost + % | Cost + amount ]
 *   [ Part price / Part markup ]   [ Labour ]
 *   [ Rounding ]                   [ Total price, worked out ]   (a group: [ Tax ], as its items' prices differ)
 *
 * The part's on the left, its labour on the right: they add up to the total (then rounded).
 *
 * How it's taxed is picked inside each money box (one setting: changing any changes them all, the
 * amounts kept as typed). Amounts are stored before tax and shown in the draft's tax terms.
 */

export const amountCheck = (label: string) => (v: string) => (MONEY_PATTERN.test(v) && Number(v) >= 0 ? null : `${label} must be an amount, like 12.50`);
const timeCheck = (unit: LabourUnit) => (v: string) => (v === "" || (Number.isFinite(Number(v)) && Number(v) >= 0) ? null : `Enter a number of ${unit}`);

/** How long an item (or a group's items) usually takes on jobs: the first of it, from how many jobs, on top of the base time per visit. */
export interface JobTimeShown {
    first: number;
    jobs: number;
    everyJob: number | null;
    /** An item on too few jobs for a time of its own: its group's instead (the group's name). */
    fromGroup?: string;
}

type TimeOf = { first: number | null; jobs: number; everyJob: number | null; leftOut?: boolean } | null;

/**
 * An item's or group's time as worked out (null: none to show: left out, or on too few jobs). An item on too few jobs: its group's
 * (`group`, with its name), if that has one; one left out has none (it isn't timed, by choice).
 */
export function jobTimeShown(time: TimeOf, group?: { time: TimeOf; name: string }): JobTimeShown | null {
    if (time?.leftOut) return null;
    if (time && time.first !== null) return { first: time.first, jobs: time.jobs, everyJob: time.everyJob };
    const own = group?.time;
    if (!own || own.first === null) return null;
    return { first: own.first, jobs: own.jobs, everyJob: own.everyJob, fromGroup: group.name };
}

/** Beside Labour: how long it usually takes on jobs (its Stats tab says more). */
function JobTimeAside({ time }: Readonly<{ time: JobTimeShown }>) {
    const base = time.everyJob === null ? "" : `, on top of the ${duration(Math.round(time.everyJob))} base time per visit`;
    const title = time.fromGroup
        ? `It's on too few jobs for a time of its own: its group's (${time.fromGroup}) items usually take this, from ${time.jobs.toLocaleString()} completed jobs${base}.`
        : `How long it usually takes, from ${time.jobs.toLocaleString()} completed jobs${base}. More on its Stats tab.`;
    return (
        <span className="text-(length:--label-text) font-normal whitespace-nowrap text-muted tabular-nums" title={title}>
            Usually {duration(Math.round(time.first))}
            {time.fromGroup ? " (from group)" : null}
        </span>
    );
}

/** Labour time's units (pricing.ts shownLabour: stored as minutes, shown in either). */
const LABOUR_UNIT_OPTIONS: { value: LabourUnit; label: string; help: string }[] = [
    { value: "minutes", label: "minutes", help: "30 for half an hour" },
    { value: "hours", label: "hours", help: "1.5 for an hour and a half" },
];


/**
 * Under Labour, on one line: what its time adds ("+$60.00 inc GST: 20 min at $180.00 an hour"),
 * or just the rate with none yet. The rate links to where it's set. `hourly` is the labour rate in
 * the same terms as `basis` ("inc GST", "before tax").
 */
export function labourHint(fmt: ReturnType<typeof useMoney>, settings: Pick<PriceListSettings, "labourRate">, hourly: number, minutes: number, basis: string) {
    // Where the rate is set (Price List settings, its Labour section), kept on one line.
    const link = (text: string) => (
        <Link href={`/t/${meta.id}/settings#labour`} className="whitespace-nowrap" title="Change the labour rate (Price List settings)">
            {text}
        </Link>
    );
    if (toNumber(settings.labourRate) <= 0) return <>{link("Set the labour rate")} in Price List settings.</>;
    const rate = link(`${fmt.money(hourly)} an hour`);
    if (minutes <= 0) return <>At {rate} {basis}.</>;
    return (
        <>
            +{fmt.money((hourly * minutes) / 60)} {basis}: {duration(minutes)} at {rate}
        </>
    );
}

export type PriceMode = PricingRule["mode"];

const MODES: { value: PriceMode; label: string; hint: string }[] = [
    { value: "manual", label: "Set price", hint: "You set the price yourself." },
    { value: "percent", label: "Cost + %", hint: "A percentage on top of the cost (before tax)." },
    { value: "fixed", label: "Cost + amount", hint: "A set amount on top of the cost." },
];

/** A kit's extra way: its parts' own prices, so each part is marked up as it is sold on its own. */
const PARTS_MODE = { value: "parts" as const, label: "Parts' prices", hint: "Its parts' own prices added up (each times how many), with a percentage on top if you like." };

const ROUNDING: { value: RoundMode; label: string }[] = [
    { value: "none", label: "No rounding" },
    { value: "up", label: "Round up to" },
    { value: "down", label: "Round down to" },
    { value: "nearest", label: "Round to the nearest" },
];

export function PricingEditor(
    props: Readonly<{
        data: PriceListData;
        /** The rule as edited ("Set price" is manual: a set price is its base price). */
        rule: PricingRule;
        /** How it's taxed, as edited. */
        tax: TaxQuote;
        canEdit: boolean;
        /**
         * The price the draft gives, worked out, shown last with its tax picked inside it. Without one
         * (a group priced from each item's cost: each item's price is its own), a Tax dropdown is
         * there instead, saying `taxNote`.
         */
        price?: string;
        taxNote?: string;
        /** Why the cost-based ways can't be chosen right now (e.g. an item with no cost). */
        costBasedUnavailable?: string;
        /**
         * An item's part cost, before tax: its part price or markup says what it comes to on it (with a link to its Costing tab, as
         * Labour's says its rate with a link to it). A group's items each have their own: none.
         */
        partCostNet?: number;
        /** Its part cost as estimated (once a cost to update is done: pricing.ts itemProfit), before tax: what its minimum markup is judged on. */
        estimatedPartNet?: number;
        onCostTab?: () => void;
        /** Open its Parts tab (a kit's parts: what its Parts' prices add up). */
        onPartsTab?: () => void;
        /** What rounding changed the price it gives by (in its basis): said under Rounding when it changes it. */
        roundedBy?: number;
        /**
         * A kit: it can be priced from its parts' prices too (Parts' prices), which add up to this, before tax (null: no parts yet,
         * so it can't). Not a kit (or a group): undefined.
         */
        partsPrice?: string | null;
        /** A draft is being edited: the editor is marked (its `footer` then its save bar). */
        editing: boolean;
        /** How long it (or a group's items) usually takes on jobs (jobTime.ts), shown by Labour: null or none, not shown. */
        jobTime?: JobTimeShown | null;
        /** On a tinted panel (an item overriding its group's price): no green editing tint (over it, it reads muddy), as its name tags. */
        tinted?: boolean;
        footer?: ReactNode;
        boxRef?: RefObject<HTMLDivElement | null>;
        onRule: (patch: Partial<PricingRule>) => Promise<unknown>;
        onMode: (mode: PriceMode) => Promise<unknown>;
        onTax: (tax: TaxQuote) => Promise<unknown>;
    }>,
) {
    const { data, rule, tax } = props;
    const fmt = useMoney();
    const rate = rateFor(tax.rateUuid, data.taxRates);
    const basis: Basis = { includesTax: tax.includesTax, tax: rate ? rate.percent / 100 : 0 };
    const label = basisLabel(tax.includesTax, rate);
    const hourly = fromNet(labourRateNet(data.settings, basis.tax), basis.includesTax, basis.tax);
    const disabled = !props.canEdit;
    const money = (field: "markupValue" | "basePrice") => shownAmount(rule[field], basis);
    // Its part's cost (an item's), in the draft's terms, linking to its Costing tab: what a part price or markup is set against.
    const partCost = props.partCostNet ?? 0;
    // A link in a hint to another of its tabs (its cost, its parts), as Labour's links to its rate.
    const tabLink = (text: string, title: string, open: (() => void) | undefined) =>
        open ? (
            <button type="button" className="cursor-pointer whitespace-nowrap text-brand-ink hover:underline" title={title} onClick={open}>
                {text}
            </button>
        ) : (
            <span className="whitespace-nowrap">{text}</span>
        );
    const costLink = props.partCostNet && props.onCostTab ? tabLink(`${fmt.money(fromNet(partCost, basis.includesTax, basis.tax))} cost`, "Its cost (the Costing tab)", props.onCostTab) : null;

    // A set price against its part's cost: "$38.05 more than its $60.91 cost", or (red) less.
    const over = fromNet(toNumber(rule.basePrice) - partCost, basis.includesTax, basis.tax);
    const againstCost =
        over < 0 ? (
            <>
                <span className="text-danger">{fmt.money(-over)} less than</span> its {costLink}
            </>
        ) : (
            <>
                {fmt.money(over)} more than its {costLink}
            </>
        );
    const judgedOn = props.estimatedPartNet ?? props.partCostNet;
    const tooLittle = judgedOn ? minimumWarning(data.settings, rule, props.price, basis, judgedOn, fmt) : undefined;

    // A draft: saved by its window's bar, so its fields don't say they're saved.
    return (
        <DraftFields>
            <div ref={props.boxRef} className={clsx("space-y-4", props.editing && "-mx-4 -mt-3.5 -mb-4 rounded-md px-4 pt-3.5 pb-4", props.editing && !props.tinted && "bg-brand-500/5")}>
                {/* Its card's heading ("Pricing") says what it is; the chosen way's hint under it. */}
                <ChoiceField<PriceMode>
                    label="Price is"
                    hideLabel
                    value={rule.mode}
                    disabled={disabled}
                    options={[
                        ...MODES.map((m) => (m.value === "manual" ? m : { ...m, unavailable: props.costBasedUnavailable })),
                        ...(props.partsPrice === undefined ? [] : [{ ...PARTS_MODE, unavailable: props.partsPrice === null ? "Parts' prices need its parts: add them on its Parts tab." : undefined }]),
                    ]}
                    onSave={props.onMode}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                    {rule.mode === "parts" ? (
                        <TextField
                            label="Parts markup"
                            value={shownPercent(rule.markupValue)}
                            suffix="%"
                            inputMode="decimal"
                            disabled={disabled}
                            validate={amountCheck("Parts markup")}
                            live
                            // What it adds on its parts' prices, and what they add up to, linking to its parts (its Parts tab), as Part markup
                            // says its cost: "+$4.50 inc GST on its $45.00 parts' prices".
                            hint={
                                props.partsPrice ? (
                                    <>
                                        +{fmt.money(fromNet((toNumber(props.partsPrice) * toNumber(rule.markupValue)) / 100, basis.includesTax, basis.tax))} {label} on its{" "}
                                        {tabLink(`${fmt.money(shownAmount(props.partsPrice, basis))} parts' prices`, "Its parts (the Parts tab)", props.onPartsTab)}
                                    </>
                                ) : undefined
                            }
                            onSave={(markupValue) => props.onRule({ markupValue })}
                        />
                    ) : null}
                    {rule.mode === "percent" ? (
                        <TextField
                            label="Part markup"
                            value={shownPercent(rule.markupValue)}
                            suffix="%"
                            inputMode="decimal"
                            disabled={disabled}
                            validate={amountCheck("Part markup")}
                            live
                            // What it adds on the part's cost, and that cost (as Labour says its time at its rate): "+$27.41 inc GST on its $60.91 cost".
                            hint={
                                costLink ? (
                                    <>
                                        +{fmt.money(fromNet((partCost * toNumber(rule.markupValue)) / 100, basis.includesTax, basis.tax))} {label} on its {costLink}
                                    </>
                                ) : undefined
                            }
                            onSave={(markupValue) => props.onRule({ markupValue })}
                        />
                    ) : null}
                    {rule.mode === "fixed" || rule.mode === "manual" ? (
                        <QuotedAmountField
                            label={rule.mode === "fixed" ? "Part markup" : "Part price"}
                            amount={money(rule.mode === "fixed" ? "markupValue" : "basePrice")}
                            quote={tax}
                            rates={data.taxRates}
                            disabled={disabled}
                            validate={amountCheck(rule.mode === "fixed" ? "Part markup" : "Part price")}
                            // What it adds on its part's cost (as the others say), or (a set price) how far it's above or below that cost
                            // (red below): with its Costing tab a click away, and the amount the other way after it.
                            hint={costLink ? rule.mode === "fixed" ? <>+{fmt.money(money("markupValue"))} {label} on its {costLink}</> : againstCost : undefined}
                            hintFirst
                            live
                            onSave={(v) => props.onRule(rule.mode === "fixed" ? { markupValue: storedAmount(v, basis) } : { basePrice: storedAmount(v, basis) })}
                            onQuote={props.onTax}
                        />
                    ) : null}
                    {/* Its unit picked inside it; switching keeps the time (90 minutes is 1.5 hours), unlike the tax. */}
                    <TextField
                        label="Labour"
                        value={shownLabour(rule.labourMinutes, rule.labourUnit)}
                        placeholder="0"
                        suffix={
                            <InlinePicker
                                label="Labour in"
                                options={LABOUR_UNIT_OPTIONS}
                                value={rule.labourUnit}
                                disabled={disabled}
                                onChange={(labourUnit) => void props.onRule({ labourUnit })}
                            />
                        }
                        inputMode="decimal"
                        disabled={disabled}
                        validate={timeCheck(rule.labourUnit)}
                        hint={labourHint(fmt, data.settings, hourly, rule.labourMinutes, label)}
                        labelAside={props.jobTime ? <JobTimeAside time={props.jobTime} /> : undefined}
                        live
                        onSave={(v) => props.onRule({ labourMinutes: storedLabour(v, rule.labourUnit) })}
                    />
                    <Rounding rule={rule} canEdit={props.canEdit} prefix={fmt.symbol} basis={label} by={props.roundedBy ? `${props.roundedBy > 0 ? "+" : "-"}${fmt.money(Math.abs(props.roundedBy))}` : undefined} onRule={props.onRule} />
                    {props.price === undefined ? (
                        <TaxSelect data={data} tax={tax} disabled={disabled} hint={props.taxNote} onTax={props.onTax} />
                    ) : (
                        // The price it gives: never typed (a set price is its base price; locked, it reads as worked out), with how it's taxed inside it.
                        <QuotedAmountField
                            label="Total price"
                            amount={props.price}
                            placeholder="-"
                            quote={tax}
                            rates={data.taxRates}
                            disabled={disabled}
                            hint={tooLittle}
                            amountLocked
                            onSave={() => Promise.resolve()}
                            onQuote={props.onTax}
                        />
                    )}
                </div>
                {props.footer}
            </div>
        </DraftFields>
    );
}

/**
 * Under the total price: its markup on its cost (the part's and its labour's, at the labour cost; the part's alone when no labour
 * cost is set, its labour as charged left out: pricing.ts itemProfit) when it's under the minimum markup (Price List settings), as
 * it's typed. Nothing otherwise.
 */
function minimumWarning(
    settings: PriceListSettings,
    rule: PricingRule,
    price: string | undefined,
    basis: Basis,
    partCostNet: number,
    fmt: ReturnType<typeof useMoney>,
) {
    if (price === undefined || price === "") return undefined;
    const minutes = rule.labourMinutes;
    const partOnly = minutes > 0 && !hasLabourCost(settings);
    const costNet = partCostNet + (partOnly ? 0 : (toNumber(settings.labourCost) * minutes) / 60);
    const charged = partOnly ? (labourRateNet(settings, basis.tax) * minutes) / 60 : 0;
    const madeNet = toNumber(price) / (basis.includesTax ? 1 + basis.tax : 1) - charged - costNet;
    const markupPct = costNet > 0 ? (madeNet / costNet) * 100 : null;
    if (!underMinimum(markupPct, settings.minMarkup) || markupPct === null) return undefined;
    // Short, so it fits on a line after the price the other way ("= $119.09 ex GST. -24% markup (min 20%)").
    let text = `${fmt.percent(Math.round(markupPct))} markup (min ${fmt.percent(settings.minMarkup)})`;
    if (settings.minMarkup <= 0) text = madeNet < 0 ? "Makes a loss" : "Makes nothing";
    return <span className="text-danger">{text}</span>;
}

/** How it's taxed, as a dropdown of its own (the same choices as the picker inside the money boxes). */
function TaxSelect(props: Readonly<{ data: PriceListData; tax: TaxQuote; disabled: boolean; hint?: string; onTax: (tax: TaxQuote) => Promise<unknown> }>) {
    const options = quoteOptions(props.data.taxRates);
    if (!options.length) return null;
    return (
        <SelectField
            label="Tax"
            value={quoteKey(props.tax, props.data.taxRates)}
            disabled={props.disabled}
            options={options}
            hint={props.hint}
            onSave={(key) => {
                const picked = options.find((o) => o.value === key);
                return picked ? props.onTax(picked.quote) : Promise.resolve();
            }}
        />
    );
}

/** How it rounds and (when it does) the step beside it, as one field, so it fits one grid cell; `basis` says which figure ("inc GST"). */
function Rounding(props: Readonly<{ rule: PricingRule; canEdit: boolean; prefix: string; basis: string; /** What it changes the price by ("+$0.05"), when it does. */ by?: string; onRule: (patch: Partial<PricingRule>) => Promise<unknown> }>) {
    const { rule } = props;
    return (
        <div className="space-y-1">
            <div className="flex items-end gap-2">
                <SelectField
                    label="Rounding"
                    value={rule.roundMode}
                    options={ROUNDING}
                    disabled={!props.canEdit}
                    onSave={(v) => props.onRule({ roundMode: v as RoundMode, roundTo: v !== "none" && toNumber(rule.roundTo) <= 0 ? "1" : rule.roundTo })}
                    className="min-w-0 flex-1"
                />
                {rule.roundMode === "none" ? null : (
                    <TextField
                        label="Steps of"
                        hideLabel
                        value={rule.roundTo}
                        prefix={props.prefix}
                        inputMode="decimal"
                        disabled={!props.canEdit}
                        validate={amountCheck("Rounding")}
                        live
                        onSave={(roundTo) => props.onRule({ roundTo })}
                        className="w-28"
                    />
                )}
            </div>
            {/* The figure it rounds: the final price, in the terms it's set (not the amount before tax). */}
            <p className="text-(length:--label-text) text-muted">
                Rounds the total price, {props.basis}
                {props.by ? `: ${props.by}` : ""}.
            </p>
        </div>
    );
}
